"use client";

import { ProcessingJobType } from "@prisma/client";
import { useEffect, useMemo } from "react";
import { Components } from "@/app/(components)/_components/Components";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import OrSeparator from "@/app/(components)/generics/OrSeparator/OrSeparator";
import Avatars from "@/app/(components)/specifics/tools/ClientAvatar/components/Avatars/Avatars";
import ExtractAvatar from "@/app/(components)/specifics/tools/ClientAvatar/components/ExtractAvatar/ExtractAvatar";
import GenerateAvatarWithAi from "@/app/(components)/specifics/tools/ClientAvatar/components/GenerateAvatarWithAi/GenerateAvatarWithAi";
import OfferRequired from "@/app/(components)/specifics/tools/Offers/components/OfferRequired/OfferRequired";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanRunPaidActions } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import PageWrapper from "@/app/(components)/specifics/wrappers/PageWrapper/PageWrapper";
import { useFetchClientAvatarBootstrap } from "@/app/hooks/fetchs/client-avatar/useFetchClientAvatarBootstrap";
import { useFetchProjectOffers } from "@/app/hooks/fetchs/offers/useFetchProjectOffers";
import { useFetchHasProjectInsights } from "@/app/hooks/fetchs/project/useFetchHasProjectInsights";
import { useFetchRunningJobs } from "@/app/hooks/fetchs/user/useFetchRunningJobs";
import { ModalStore } from "@/app/stores/ModalStore";
import { OfferStore } from "@/app/stores/OfferStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ClientAvatarBootstrap } from "@/app/types/client-avatar/ClientAvatarBootstrap";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { PageBootstrap } from "@/app/types/common/WorkspaceSelection";
import { AvatarExtractionJobMetadata } from "@/app/types/job/ProcessingJob";
import { Limits } from "@/app/vars/Limits";
import styles from "./ClientAvatar.module.css";

type Props = DefaultProps & {
  bootstrap: PageBootstrap<ClientAvatarBootstrap> | null;
};

export default function ClientAvatar(props: Props) {
  const { params, bootstrap } = props;
  const { lang } = params;

  const T = useT();
  const user = useAuthUser();
  const canRunPaidActions = useCanRunPaidActions();
  const project = ProjectStore((state) => state.currentProject);
  const projects = ProjectStore((state) => state.projects);
  const setCurrentProject = ProjectStore((state) => state.setCurrentProject);
  const projectsFetched = ProjectStore((state) => state.projectsFetched);

  const currentOfferId = OfferStore((state) => state.currentOfferId);
  const setStoreOffers = OfferStore((state) => state.setOffers);
  const selectedOfferId = currentOfferId ? String(currentOfferId) : "";

  const initialBootstrap = bootstrap?.projectId === project?.id ? bootstrap?.data : undefined;

  const { isSuccess: bootstrapReady } = useFetchClientAvatarBootstrap({
    projectId: project?.id,
    enabled: !!user && !!project?.id,
    initialData: initialBootstrap,
  });

  const { data: hasProjectInsightsFresh } = useFetchHasProjectInsights(
    project?.id,
    currentOfferId,
    bootstrapReady,
  );
  const hasInsights = hasProjectInsightsFresh ?? false;
  const { data: fetchedOffers } = useFetchProjectOffers({
    projectId: project?.id,
    enabled: !!user && !!project?.id && bootstrapReady,
  });

  const offers = useMemo(() => fetchedOffers ?? [], [fetchedOffers]);
  const offersLoaded = fetchedOffers !== undefined;

  const selectedOfferHasAvatar =
    (offers.find((o) => o.id.toString() === selectedOfferId)?._count?.clientAvatars ?? 0) > 0;

  useEffect(() => {
    if (offersLoaded && project?.id) {
      setStoreOffers(project.id, offers);
    }
  }, [offersLoaded, offers, project?.id, setStoreOffers]);

  const { data: runningJobs } = useFetchRunningJobs({ enabled: !!user });

  const analyzingOfferIds = useMemo(() => {
    if (!runningJobs) return [];
    return runningJobs
      .filter((j) => j.type === ProcessingJobType.CLIENT_AVATAR)
      .map((j) => (j.metadata as AvatarExtractionJobMetadata | null)?.offerId)
      .filter(Boolean) as number[];
  }, [runningJobs]);

  const isAnyAvatarExtracting = useMemo(() => {
    if (!runningJobs) return false;
    return runningJobs.some((j) => j.type === ProcessingJobType.CLIENT_AVATAR);
  }, [runningJobs]);

  const isCurrentOfferAnalyzing = selectedOfferId
    ? analyzingOfferIds.includes(parseInt(selectedOfferId, 10))
    : false;

  const isReady = !user || (projectsFetched && (!project?.id || bootstrapReady));

  const openExtractionModal = (isEnrich: boolean, rebuild = false) => {
    if (!project?.id || !selectedOfferId) {
      return;
    }

    ModalStore.getState().openModal(
      Components.AvatarExtraction,
      T(rebuild ? TKey.CLIENT_AVATAR_REEXTRACT_TITLE : TKey.CLIENT_AVATAR_EXTRACT_TITLE) as string,
      {
        params,
        projectId: project.id,
        offerId: parseInt(selectedOfferId, 10),
        isEnrich,
        hasInsights,
        rebuild,
      },
      ModalType.SMALL,
    );
  };

  const openAddProject = () => {
    ModalStore.getState().openModal(
      Components.ProjectForm,
      T(TKey.PROJECT_ADD),
      {
        lang,
        params,
        isCreating: true,
        onAfterSave: () => openExtractionModal(false),
      },
      ModalType.SMALL,
    );
  };

  const handleExtract = async () => {
    const activeProject = project ?? projects[0];

    if (!activeProject?.id) return;

    if (!project) {
      setCurrentProject(activeProject);
    }

    if (!activeProject.name.trim()) {
      openAddProject();
      return;
    }

    openExtractionModal(false);
  };

  const analyzingBadge = (
    <div className={styles.analyzingBadge}>
      <CircleLoader size={16} color="var(--color-green-3)" strokeWidth={3} />
      <span className={styles.analyzingText}>{T(TKey.CLIENT_AVATAR_ANALYZING)}</span>
    </div>
  );

  return (
    <PageWrapper title={T(TKey.CLIENT_AVATAR_TITLE)} subtitle={T(TKey.CLIENT_AVATAR_SUBTITLE)}>
      <div className={styles.container}>
        {!user ? (
          <>
            <div className={styles.emptyActions}>
              <ExtractAvatar
                params={params}
                atLeastOneAnalysedCall={undefined}
                offers={undefined}
                onExtract={handleExtract}
                isAnalyzing={isAnyAvatarExtracting}
              />
              <GenerateAvatarWithAi params={params} />
            </div>
            <Avatars params={params} />
          </>
        ) : !isReady ? (
          <div className={styles.loaderContainer}>
            <PointsLoader color="var(--color-green-3)" delay={Limits.CLIENT_AVATAR_LOADER_DELAY} />
          </div>
        ) : (
          <div className={styles.avatarsWrapper}>
            {offers && offers.length > 0 ? (
              <>
                {isCurrentOfferAnalyzing && (
                  <div className={styles.offerSelector}>{analyzingBadge}</div>
                )}
                {selectedOfferHasAvatar || isCurrentOfferAnalyzing ? (
                  <Avatars
                    params={params}
                    offerId={parseInt(selectedOfferId, 10)}
                    isAnalyzing={isCurrentOfferAnalyzing}
                    onExtract={openExtractionModal}
                    enabled={bootstrapReady}
                  />
                ) : canRunPaidActions ? (
                  <div className={styles.emptyActions}>
                    <ExtractAvatar
                      params={params}
                      atLeastOneAnalysedCall={hasInsights}
                      offers={offers}
                      onExtract={handleExtract}
                      isAnalyzing={isAnyAvatarExtracting}
                    />
                    <OrSeparator />
                    <GenerateAvatarWithAi params={params} />
                  </div>
                ) : (
                  <p className={styles.notGeneratedMessage}>
                    {T(TKey.CLIENT_AVATAR_NOT_GENERATED_MEMBER)}
                  </p>
                )}
              </>
            ) : isAnyAvatarExtracting ? (
              <Avatars params={params} isAnalyzing={true} />
            ) : (
              <div className={styles.emptyActions}>
                {project && <OfferRequired params={params} />}
              </div>
            )}
          </div>
        )}
      </div>
    </PageWrapper>
  );
}
